/**
 * "Connect this repo to Docit" (2026-09-28). The VS Code extension reads the folder's git remote,
 * branch and Docit project, and opens `/repositories?connect=…&branch=…&project=…&level=…` in the
 * repo's own workspace. This module is the pure half: read those params, and decide which
 * installation (if any) already covers the remote. The dialog does the rest.
 *
 * DOM-free and React-free on purpose (unit-tested in prefill.test.ts).
 */

export type ConnectLevel = "project" | "workspace";

export interface ConnectPrefill {
  /** The remote as given: an https / ssh / scp-style URL, or `owner/name`. */
  remote: string;
  branch: string | null;
  /** A project id (or name) the connection belongs to. */
  project: string | null;
  /** `workspace` forces a workspace-level connection even when a project is given. */
  level: ConnectLevel | null;
}

/** The search params this module owns — stripped from the URL once the dialog closes. */
export const PREFILL_PARAMS = ["connect", "branch", "project", "level"] as const;

/** The prefill in a location search string, or null when there is no `connect=`. */
export function parseConnectPrefill(search: string): ConnectPrefill | null {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return null;
  }
  const remote = params.get("connect")?.trim();
  if (!remote) {
    return null;
  }
  const clean = (value: string | null) => {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  };
  const level = params.get("level")?.trim().toLowerCase();
  return {
    remote,
    branch: clean(params.get("branch")),
    project: clean(params.get("project")),
    level: level === "project" || level === "workspace" ? level : null,
  };
}

export interface RemoteName {
  /** Lower-cased host, or null for a bare `owner/name`. */
  host: string | null;
  owner: string;
  name: string;
  /** `owner/name` as written (case kept, `.git` stripped). */
  fullName: string;
  /** Lower-cased `owner/name` — the comparison key. */
  key: string;
}

/**
 * `owner/name` out of any common remote spelling:
 *   https://github.com/Owner/Name(.git)(/)   ssh://git@github.com/Owner/Name.git
 *   git@github.com:Owner/Name.git            Owner/Name
 * Returns null for anything that is not one repository (a mount path, a bare word).
 */
export function parseRemote(remote: string): RemoteName | null {
  const raw = (remote ?? "").trim();
  if (!raw) {
    return null;
  }
  let host: string | null = null;
  let path: string;
  const scp = /^[\w.-]+@([\w.-]+):(?!\/\/)(.+)$/.exec(raw);
  if (scp) {
    host = scp[1] ?? null;
    path = scp[2] ?? "";
  } else if (/^[a-z][a-z0-9+.-]*:\/\//i.test(raw)) {
    let url: URL;
    try {
      url = new URL(raw);
    } catch {
      return null;
    }
    host = url.hostname;
    path = url.pathname;
  } else if (raw.startsWith("/") || raw.startsWith(".")) {
    return null; // a filesystem path (a mount), not a hosted repository
  } else {
    path = raw;
  }
  const segments = path
    .replace(/\/+$/, "")
    .replace(/\.git$/i, "")
    .split("/")
    .filter(Boolean);
  if (segments.length !== 2) {
    return null;
  }
  const [owner, name] = segments as [string, string];
  const fullName = `${owner}/${name}`;
  return {
    host: host ? host.toLowerCase() : null,
    owner,
    name,
    fullName,
    key: fullName.toLowerCase(),
  };
}

/** Same repository? Owner/name compared case-insensitively; hosts only when both are known. */
export function sameRepository(a: string, b: string): boolean {
  const left = parseRemote(a);
  const right = parseRemote(b);
  if (!left || !right) {
    return false;
  }
  if (left.host && right.host && left.host !== right.host) {
    return false;
  }
  return left.key === right.key;
}

/** The https URL for a remote (what the `public` kind wants); a bare `owner/name` means GitHub. */
export function httpsRemote(remote: string): string | null {
  const parsed = parseRemote(remote);
  if (!parsed) {
    return null;
  }
  return `https://${parsed.host ?? "github.com"}/${parsed.fullName}`;
}

/** Is this remote on GitHub (the only host the GitHub App path can cover)? */
export function isGitHubRemote(remote: string): boolean {
  const parsed = parseRemote(remote);
  return parsed !== null && (parsed.host === null || parsed.host === "github.com");
}

export interface RepositoryCandidate {
  fullName: string;
  url: string;
  defaultBranch: string;
  private: boolean;
}

export interface InstallationRepositories {
  installationId: string;
  repositories: RepositoryCandidate[] | undefined;
}

export interface InstallationMatch {
  installationId: string;
  repository: RepositoryCandidate;
}

/** The first installation whose repositories include the remote (by fullName, else by url). */
export function findInstallationFor(
  remote: string,
  installations: InstallationRepositories[],
): InstallationMatch | null {
  const target = parseRemote(remote);
  if (!target) {
    return null;
  }
  for (const installation of installations) {
    for (const repository of installation.repositories ?? []) {
      if (
        repository.fullName.toLowerCase() === target.key ||
        sameRepository(repository.url, remote)
      ) {
        return { installationId: installation.installationId, repository };
      }
    }
  }
  return null;
}

/** The project a prefill names — by id first, then by name (case-insensitive). */
export function resolvePrefillProject<P extends { id: string; name: string }>(
  prefill: Pick<ConnectPrefill, "project" | "level"> | null,
  projects: P[] | undefined,
): P | null {
  if (!prefill?.project || prefill.level === "workspace") {
    return null;
  }
  const wanted = prefill.project.toLowerCase();
  const list = projects ?? [];
  return (
    list.find((p) => p.id.toLowerCase() === wanted) ??
    list.find((p) => p.name.toLowerCase() === wanted) ??
    null
  );
}

/** `search` without the prefill params (and the leading `?` only when something is left). */
export function stripPrefill(search: string): string {
  const params = new URLSearchParams(search);
  for (const key of PREFILL_PARAMS) {
    params.delete(key);
  }
  const rest = params.toString();
  return rest ? `?${rest}` : "";
}
